import React, { useState, useEffect } from 'react';
import { BsPerson } from 'react-icons/bs';
import { useCustomer } from './CustomerContext';
import { apiFetch, parseJsonSafe } from '../../utils/api';

const CustomerProfilePage = () => {
  const { customer, loading, authHeaders, refreshData } = useCustomer();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdErr, setPwdErr] = useState('');

  useEffect(() => {
    if (customer?.name) setName(customer.name);
  }, [customer?.name]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setProfileMsg('');
    setProfileErr('');
    try {
      const res = await apiFetch('/api/v1/software/customer/portal/profile', {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data?.message || 'No se pudo actualizar, intentalo de nuevo');
      setProfileMsg('Perfil actualizado');
      refreshData();
    } catch (err) {
      setProfileErr(err.message);
    } finally {
      setSaving(false);
    }
  };

  const newPwdTooShort = newPwd.length > 0 && newPwd.length < 8;
  const pwdMismatch = confirmPwd.length > 0 && newPwd !== confirmPwd;

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      setPwdErr('La nueva contraseña debe tener un mínimo de 8 caracteres');
      return;
    }
    if (newPwd !== confirmPwd) {
      setPwdErr('Las contraseñas no coinciden');
      return;
    }
    setPwdSaving(true);
    setPwdMsg('');
    setPwdErr('');
    try {
      const res = await apiFetch('/api/v1/software/customer/portal/password', {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: currentPwd, new_password: newPwd })
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data?.message || 'No se pudo cambiar la contraseña, intentalo de nuevo');
      setPwdMsg('Contraseña actualizada correctamente. Por seguridad se han cerrado el resto de sesiones; tendrás que volver a iniciar sesión en otros dispositivos.');
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (err) {
      setPwdErr(err.message);
    } finally {
      setPwdSaving(false);
    }
  };

  if (loading) return <div className="cp-skeleton cp-skeleton-card" style={{ height: 200 }} />;

  return (
    <div>
      <h1 className="cp-page-title">Perfil</h1>

      <div className="cp-two-col">
        <div className="cp-card-static">
          <div className="cp-card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div className="cp-avatar" style={{ width: 48, height: 48, fontSize: 18 }}>
                {(customer?.name || customer?.email || '?')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>{customer?.name || 'Cliente'}</div>
                <div style={{ fontSize: 13, color: '#888' }}>{customer?.email}</div>
              </div>
            </div>

            {profileErr && <div className="cp-alert cp-alert-error" role="alert">{profileErr}</div>}
            {profileMsg && <div className="cp-alert cp-alert-success" role="alert">{profileMsg}</div>}

            <form onSubmit={saveProfile}>
              <label className="cp-label" htmlFor="cp-profile-name">Nombre</label>
              <input id="cp-profile-name" className="cp-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" />
              <label className="cp-label" htmlFor="cp-profile-email" style={{ marginTop: 12 }}>Email</label>
              <input id="cp-profile-email" className="cp-input" value={customer?.email || ''} disabled style={{ background: '#f5f5f5', color: '#999' }} />
              <button type="submit" className="cp-btn cp-btn-primary cp-btn-sm" style={{ marginTop: 16 }} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </form>
          </div>
        </div>

        <div className="cp-card-static">
          <div className="cp-card-body">
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <BsPerson /> Cambiar contraseña
            </h3>

            {pwdErr && <div className="cp-alert cp-alert-error" role="alert">{pwdErr}</div>}
            {pwdMsg && <div className="cp-alert cp-alert-success" role="alert">{pwdMsg}</div>}

            <form onSubmit={changePassword}>
              <label className="cp-label" htmlFor="cp-current-pwd">Contraseña actual</label>
              <input id="cp-current-pwd" className="cp-input" type="password" autoComplete="current-password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} required />

              <label className="cp-label" htmlFor="cp-new-pwd" style={{ marginTop: 12 }}>Nueva contraseña</label>
              <input id="cp-new-pwd" className="cp-input" type="password" autoComplete="new-password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={8} aria-describedby="cp-new-pwd-hint" />
              <div id="cp-new-pwd-hint" style={{ fontSize: 12, marginTop: 4, color: newPwdTooShort ? '#c0392b' : '#888' }}>
                {newPwdTooShort ? 'Mínimo 8 caracteres (te faltan ' + (8 - newPwd.length) + ')' : 'Mínimo 8 caracteres'}
              </div>

              <label className="cp-label" htmlFor="cp-confirm-pwd" style={{ marginTop: 12 }}>Confirmar contraseña</label>
              <input id="cp-confirm-pwd" className="cp-input" type="password" autoComplete="new-password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required minLength={8} aria-describedby={pwdMismatch ? 'cp-confirm-pwd-hint' : undefined} />
              {pwdMismatch && (
                <div id="cp-confirm-pwd-hint" style={{ fontSize: 12, marginTop: 4, color: '#c0392b' }}>
                  Las contraseñas no coinciden
                </div>
              )}

              <p style={{ fontSize: 12, color: '#888', marginTop: 14, marginBottom: 0 }}>
                Por seguridad, al cambiar la contraseña se cerrarán el resto de sesiones activas y tendrás que volver a iniciar sesión en tus otros dispositivos.
              </p>

              <button type="submit" className="cp-btn cp-btn-outline cp-btn-sm" style={{ marginTop: 16 }} disabled={pwdSaving || newPwdTooShort || pwdMismatch}>
                {pwdSaving ? 'Actualizando...' : 'Cambiar contraseña'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfilePage;
